<script setup lang="ts">
import { computed, toRef, useAttrs, useSlots } from 'vue';

import { KaDescription } from '@/kasten-components/components/KaDescription';
import type {
  KaVeeFormFieldCheckboxOrRadioPropsContract,
  KaVeeFormFieldWithoutInputEmitContract,
} from '@/kasten-components/components/KaVeeFormField';
import { useDataTestid } from '@/kasten-components/composables';
import type { HTMLElementEvent, VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

export type KaCheckboxProps = Omit<KaVeeFormFieldCheckboxOrRadioPropsContract, 'errorMessage'> & {
  modelValue: boolean;
  name: string;
  id?: string;
  dataTestid?: string;
  label: string;
  hideLabel?: boolean;
  description?: string;
  hideDescription?: boolean;
  indeterminate?: boolean;
  containerClasses?: VueClass;
  labelClasses?: VueClass;
  required?: boolean;
  ariaRequired?: boolean;
};

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<KaCheckboxProps>(), {
  value: 'on',
});

// prettier-ignore
const emit = defineEmits<KaVeeFormFieldWithoutInputEmitContract<HTMLElementEvent<HTMLInputElement>> & { (e: 'update:modelValue', value: boolean): void; (e: 'change', event: HTMLElementEvent<HTMLInputElement>): void; (e: 'blur', event: FocusEvent): void; }>();

const slots = useSlots();

const attrs = useAttrs();

const name = toRef(props, 'name');
const id = computed(() => props.id ?? name.value);
const descriptionId = computed(() => (props.description ? `${id.value}-description` : undefined));
const labelId = computed(() => `${id.value}-label`);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));

const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);

const checked = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit('update:modelValue', val);
  },
});

const isRequired = computed(() => props.required || props.ariaRequired);
</script>

<template>
  <div
    :class="
      cnMerge(
        'relative flex',
        (props.description || $slots.description) && !props.hideDescription
          ? 'items-start'
          : 'items-center',
        props.containerClasses,
      )
    "
  >
    <div class="flex h-6 items-center">
      <input
        :id="id"
        v-model="checked"
        :indeterminate="indeterminate"
        :data-testid="dataTestid"
        :name="name"
        :value="props.value"
        type="checkbox"
        class="form-checkbox h-4 w-4 rounded border-gray-300 bg-gray-200 text-blue-500 checked:bg-blue-500 focus:ring-blue-500 dark:border-white/20 dark:bg-white/10 dark:ring-offset-navy-900 dark:checked:border-current dark:checked:bg-current"
        v-bind="attrs"
        @change="emit('change', $event as HTMLElementEvent<HTMLInputElement>)"
        @blur="emit('blur', $event)"
        :aria-required="isRequired"
      />
    </div>
    <label
      :id="labelId"
      :for="id"
      :class="
        cnMerge('m-0 ml-3 select-none p-0', {
          'sr-only': props.hideLabel && !shouldDisplayDescription,
        })
      "
      :data-testid="suffix('label-container')"
    >
      <span
        :data-testid="suffix('label')"
        :class="
          cnMerge([
            'flex items-center text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50',
            { 'sr-only': props.hideLabel },
            props.labelClasses,
          ])
        "
      >
        <slot name="label">
          {{ props.label }}
        </slot>
      </span>

      <KaDescription
        v-if="props.description || $slots.description"
        :id="descriptionId"
        :data-testid="suffix('description')"
        :description="props.description"
        :hide-description="props.hideDescription"
        class="m-0 block p-0 font-normal"
      >
        <slot name="description" v-bind="{ description: props.description }" />
      </KaDescription>
    </label>
  </div>
</template>
